/**
 * KillSwitch — Stop Trading / Start Trading toggle for the Header.
 *
 * Compact pill showing LIVE (green) or STOPPED (red) with a toggle.
 * Opens confirmation dialog before toggling.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Shield, ShieldOff } from 'lucide-react';
import tradingStatusService from '../../services/tradingStatusService';
import EnableToggle from '../ui/EnableToggle';
import FocusedDialog from '../ui/FocusedDialog';

interface KillSwitchProps {
  tradingEnabled: boolean;
  onToggle: (enabled: boolean) => void;
}

const KillSwitch: React.FC<KillSwitchProps> = ({ tradingEnabled, onToggle }) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const newStatus = await tradingStatusService.setStatus(!tradingEnabled);
      onToggle(newStatus);
    } catch (e: any) {
      console.error('Failed to toggle trading status:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <div className="hidden sm:flex">
        <EnableToggle
          enabled={tradingEnabled}
          loading={loading}
          onChange={handleClick}
          size="md"
          showLabel
          enabledLabel="LIVE"
          disabledLabel="STOPPED"
          ariaLabel={tradingEnabled ? 'Stop trading' : 'Resume trading'}
        />
      </div>

      {/* Confirmation Dialog — rendered via portal to escape Header stacking context */}
      {confirmOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <FocusedDialog
            ariaLabel={tradingEnabled ? 'Stop trading' : 'Resume trading'}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${tradingEnabled ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                {tradingEnabled ? (
                  <ShieldOff className="w-5 h-5 text-red-600 dark:text-red-400" />
                ) : (
                  <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {tradingEnabled ? 'Stop Trading?' : 'Resume Trading?'}
              </h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              {tradingEnabled
                ? 'This will stop ALL automated trading immediately. No new orders will be placed — entries, exits, stop-losses, and monitoring will all be paused. You will need to manually close any open positions from the Positions page.'
                : 'This will resume all automated trading. Scheduled entries, exits, stop-loss monitoring, and order sync will restart on the next minute tick.'}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  tradingEnabled
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {tradingEnabled ? 'Stop Trading' : 'Resume Trading'}
              </button>
            </div>
          </FocusedDialog>
        </div>,
        document.body
      )}
    </>
  );
};

export default KillSwitch;
